import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { knowledgeBaseApi, teamsApi } from '@/lib/api';
import { eventEmitter, EVENTS } from '@/lib/events';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Plus,
  BookOpen,
  Upload,
  X,
  FileText,
  Loader2,
  HelpCircle,
  Search,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { CollectionListView } from '@/components/knowledge-base/CollectionListView';
import { DocumentListView } from '@/components/knowledge-base/DocumentListView';
import { KnowledgeBasePageSkeleton } from '@/components/knowledge-base/KnowledgeBaseSkeletons';
import { EditCollectionDialog } from '@/components/dialogs/EditCollectionDialog';
import { ShareCollectionDialog } from '@/components/dialogs/ShareCollectionDialog';
import type { KbCollection, KbDocument } from '@/types/knowledge-base';

interface Team {
  id: string;
  name: string;
  description?: string;
}

export default function KnowledgeBase() {
  // URL parameters
  const [searchParams, setSearchParams] = useSearchParams();
  const collectionIdFromUrl = searchParams.get('collection');

  // View state
  const [currentView, setCurrentView] = useState<'collections' | 'documents'>('collections');

  // Collections state
  const [collections, setCollections] = useState<KbCollection[]>([]);
  const [selectedCollection, setSelectedCollection] = useState<KbCollection | null>(null);
  const [loadingCollections, setLoadingCollections] = useState(true);

  // Documents state
  const [documents, setDocuments] = useState<KbDocument[]>([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);

  // Teams state
  const [teams, setTeams] = useState<Team[]>([]);

  // Search state for collections
  const [collectionSearchQuery, setCollectionSearchQuery] = useState('');

  // Dialog states
  const [showCreateCollectionDialog, setShowCreateCollectionDialog] = useState(false);
  const [showUploadDocumentDialog, setShowUploadDocumentDialog] = useState(false);
  const [editCollectionDialogOpen, setEditCollectionDialogOpen] = useState(false);
  const [collectionToEdit, setCollectionToEdit] = useState<KbCollection | null>(null);
  const [deleteCollectionDialogOpen, setDeleteCollectionDialogOpen] = useState(false);
  const [collectionToDelete, setCollectionToDelete] = useState<KbCollection | null>(null);
  const [deleteDocumentDialogOpen, setDeleteDocumentDialogOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<KbDocument | null>(null);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [collectionToShare, setCollectionToShare] = useState<KbCollection | null>(null);

  // Form states
  const [newCollectionForm, setNewCollectionForm] = useState({
    name: '',
    description: '',
    embeddingModel: 'text-embedding-3-small',
  });

  const [uploadDocumentForm, setUploadDocumentForm] = useState({
    name: '',
    content: '',
    sourceType: 'upload',
    fileType: 'txt',
    chunkSize: 1000,
    chunkOverlap: 200,
  });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [contentTab, setContentTab] = useState<'upload' | 'paste'>('upload');

  // Operation states
  const [creatingCollection, setCreatingCollection] = useState(false);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [rebuildingIndex, setRebuildingIndex] = useState(false);

  // Load data on mount
  useEffect(() => {
    loadCollections();
    loadTeams();
  }, []);

  // Handle collection selection from URL
  useEffect(() => {
    if (collectionIdFromUrl && collections.length > 0) {
      const collection = collections.find((c) => c.id === collectionIdFromUrl);
      if (collection) {
        setSelectedCollection(collection);
        setCurrentView('documents');
      }
    } else if (!collectionIdFromUrl) {
      setSelectedCollection(null);
      setCurrentView('collections');
    }
  }, [collectionIdFromUrl, collections]);

  // Load documents when collection is selected
  useEffect(() => {
    if (selectedCollection) {
      loadDocuments(selectedCollection.id);
    } else {
      setDocuments([]);
    }
  }, [selectedCollection]);

  // Handle ?create=true query parameter to open create dialog
  useEffect(() => {
    if (searchParams.get('create') === 'true') {
      setShowCreateCollectionDialog(true);
      // Remove the query parameter from URL
      searchParams.delete('create');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const loadCollections = async () => {
    try {
      setLoadingCollections(true);
      const response = await knowledgeBaseApi.getCollections();
      const collectionsData = Array.isArray(response.data)
        ? response.data
        : response.data?.data || [];
      setCollections(collectionsData);
      // Refresh selected collection if needed
      if (selectedCollection) {
        const updated = collectionsData.find((c: KbCollection) => c.id === selectedCollection.id);
        if (updated) {
          setSelectedCollection(updated);
        }
      }
    } catch (error) {
      console.error('Failed to load collections:', error);
      toast.error('Failed to load collections');
      setCollections([]);
    } finally {
      setLoadingCollections(false);
    }
  };

  const loadDocuments = async (collectionId: string) => {
    try {
      setLoadingDocuments(true);
      const response = await knowledgeBaseApi.getDocuments(collectionId);
      const data = response.data;
      if (Array.isArray(data)) {
        setDocuments(data);
      } else if (data?.isSuccess && data?.data) {
        setDocuments(data.data);
      } else if (data?.data) {
        setDocuments(data.data);
      } else {
        setDocuments([]);
      }
    } catch (error) {
      console.error('Failed to load documents:', error);
      toast.error('Failed to load documents');
      setDocuments([]);
    } finally {
      setLoadingDocuments(false);
    }
  };

  const loadTeams = async () => {
    try {
      const response = await teamsApi.list();
      if (response.data) {
        const teamsList = Array.isArray(response.data) ? response.data : response.data.data || [];
        setTeams(teamsList);
      }
    } catch (error) {
      console.error('Failed to load teams:', error);
    }
  };

  // Handle collection click (drill-down)
  const handleCollectionClick = (collection: KbCollection) => {
    setSelectedCollection(collection);
    setCurrentView('documents');
    setSearchParams({ collection: collection.id });
  };

  // Handle back from documents view
  const handleBack = () => {
    setSelectedCollection(null);
    setCurrentView('collections');
    setSearchParams({});
  };

  // Collection CRUD handlers
  const handleCreateCollection = async () => {
    if (!newCollectionForm.name.trim()) {
      toast.error('Collection name is required');
      return;
    }

    try {
      setCreatingCollection(true);
      await knowledgeBaseApi.createCollection({
        name: newCollectionForm.name,
        description: newCollectionForm.description || undefined,
        visibility: 'private',
        embeddingModel: newCollectionForm.embeddingModel,
      });
      toast.success('Collection created successfully');
      setShowCreateCollectionDialog(false);
      setNewCollectionForm({
        name: '',
        description: '',
        embeddingModel: 'text-embedding-3-small',
      });
      loadCollections();
      eventEmitter.emit(EVENTS.KB_COLLECTION_CREATED);
    } catch (error: any) {
      console.error('Failed to create collection:', error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        'Failed to create collection';
      toast.error(errorMessage);
    } finally {
      setCreatingCollection(false);
    }
  };

  const handleDeleteCollection = async () => {
    if (!collectionToDelete) return;

    try {
      await knowledgeBaseApi.deleteCollection(collectionToDelete.id);
      toast.success('Collection deleted successfully');
      setDeleteCollectionDialogOpen(false);
      setCollectionToDelete(null);
      if (selectedCollection?.id === collectionToDelete.id) {
        setSelectedCollection(null);
        setCurrentView('collections');
        setSearchParams({});
      }
      loadCollections();
      eventEmitter.emit(EVENTS.KB_COLLECTION_DELETED);
    } catch (error: any) {
      console.error('Failed to delete collection:', error);
      toast.error('Failed to delete collection');
    }
  };

  // Document handlers
  const handleUploadDocument = async () => {
    if (!selectedCollection) return;
    if (!uploadDocumentForm.name.trim()) {
      toast.error('Document name is required');
      return;
    }

    const binaryExtensions = ['pdf', 'docx'];
    const isBinaryFile = selectedFile && binaryExtensions.includes(uploadDocumentForm.fileType);

    if (!isBinaryFile && !uploadDocumentForm.content.trim()) {
      toast.error('Document content is required');
      return;
    }

    try {
      setUploadingDocument(true);

      if (isBinaryFile && selectedFile) {
        await knowledgeBaseApi.uploadDocumentFile(selectedCollection.id, selectedFile, {
          name: uploadDocumentForm.name,
          chunkSize: uploadDocumentForm.chunkSize,
          chunkOverlap: uploadDocumentForm.chunkOverlap,
        });
      } else {
        await knowledgeBaseApi.uploadDocument(selectedCollection.id, {
          name: uploadDocumentForm.name,
          content: uploadDocumentForm.content,
          sourceType: 'upload',
          fileType: uploadDocumentForm.fileType,
          chunkSize: uploadDocumentForm.chunkSize,
          chunkOverlap: uploadDocumentForm.chunkOverlap,
        });
      }

      toast.success('Document uploaded - indexing in progress...');
      setShowUploadDocumentDialog(false);
      setUploadDocumentForm({
        name: '',
        content: '',
        sourceType: 'upload',
        fileType: 'txt',
        chunkSize: 1000,
        chunkOverlap: 200,
      });
      setSelectedFile(null);
      // Immediately reload to show the new document with indexing status
      loadDocuments(selectedCollection.id);
      loadCollections();
      // Poll for status updates while indexing
      const pollInterval = setInterval(async () => {
        try {
          const response = await knowledgeBaseApi.getDocuments(selectedCollection.id);
          const docs = Array.isArray(response.data) ? response.data : response.data?.data || [];
          setDocuments(docs);
          // Check if any document is still indexing
          const stillIndexing = docs.some((d: KbDocument) =>
            d.status === 'processing' || d.status === 'indexing'
          );
          if (!stillIndexing) {
            clearInterval(pollInterval);
            loadCollections(); // Refresh to get updated chunk counts
          }
        } catch {
          clearInterval(pollInterval);
        }
      }, 2000);
      // Stop polling after 2 minutes max (large documents may take longer)
      setTimeout(() => clearInterval(pollInterval), 120000);
    } catch (error: any) {
      console.error('Failed to upload document:', error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        'Failed to upload document';
      toast.error(errorMessage);
    } finally {
      setUploadingDocument(false);
    }
  };

  const handleDeleteDocument = async () => {
    if (!documentToDelete || !selectedCollection) return;

    try {
      await knowledgeBaseApi.deleteDocument(selectedCollection.id, documentToDelete.id);
      toast.success('Document deleted successfully');
      setDeleteDocumentDialogOpen(false);
      setDocumentToDelete(null);
      loadDocuments(selectedCollection.id);
      loadCollections();
    } catch (error: any) {
      console.error('Failed to delete document:', error);
      toast.error('Failed to delete document');
    }
  };

  const handleRebuildIndex = async () => {
    if (!selectedCollection) return;

    try {
      setRebuildingIndex(true);
      await knowledgeBaseApi.rebuildIndex(selectedCollection.id);
      toast.success('Index rebuilt successfully');
    } catch (error: any) {
      console.error('Failed to rebuild index:', error);
      toast.error('Failed to rebuild index');
    } finally {
      setRebuildingIndex(false);
    }
  };

  // File handling
  const processFile = async (file: File) => {
    const textTypes = ['text/plain', 'text/markdown', 'text/csv', 'application/json', 'text/xml', 'text/html'];
    const textExtensions = ['.txt', '.md', '.csv', '.json', '.xml', '.html'];
    const binaryExtensions = ['.pdf', '.docx'];
    const allAllowedExtensions = [...textExtensions, ...binaryExtensions];
    const fileExt = '.' + file.name.split('.').pop()?.toLowerCase();

    if (!textTypes.includes(file.type) && !allAllowedExtensions.includes(fileExt)) {
      toast.error('Unsupported file type. Please upload PDF, DOCX, TXT, MD, CSV, JSON, XML, or HTML files.');
      return;
    }

    const isBinaryFile = binaryExtensions.includes(fileExt);
    const maxSize = isBinaryFile ? 10 * 1024 * 1024 : 5 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error(`File too large. Maximum size is ${isBinaryFile ? '10MB' : '5MB'}.`);
      return;
    }

    try {
      setSelectedFile(file);

      if (isBinaryFile) {
        setUploadDocumentForm((prev) => ({
          ...prev,
          name: prev.name || file.name.replace(/\.[^/.]+$/, ''),
          content: '',
          fileType: fileExt.replace('.', '') || 'pdf',
        }));
      } else {
        const content = await file.text();
        setUploadDocumentForm((prev) => ({
          ...prev,
          name: prev.name || file.name.replace(/\.[^/.]+$/, ''),
          content: content,
          fileType: fileExt.replace('.', '') || 'txt',
        }));
      }
    } catch (error) {
      console.error('Failed to read file:', error);
      toast.error('Failed to read file');
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      await processFile(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      await processFile(file);
    }
  };

  // Show loading state
  if (loadingCollections && collections.length === 0) {
    return <KnowledgeBasePageSkeleton />;
  }

  return (
    <div className="h-full flex flex-col overflow-hidden p-4 md:p-6">
      {/* Header */}
      <div className="flex-none mb-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold flex items-center gap-2">
              <BookOpen className="w-6 h-6" />
              Knowledge Base
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Manage document collections for semantic search in your workflows
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      {currentView === 'collections' ? (
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Header row with search and actions */}
          <div className="flex-none flex items-center justify-between mb-4 gap-4">
            <div className="relative w-[250px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search collections..."
                value={collectionSearchQuery}
                onChange={(e) => setCollectionSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <Button onClick={() => setShowCreateCollectionDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              New Collection
            </Button>
          </div>

          {/* Collections List */}
          <div className="flex-1 overflow-hidden">
            <CollectionListView
              collections={collections}
              loading={loadingCollections}
              searchQuery={collectionSearchQuery}
              onCollectionClick={handleCollectionClick}
              onEditCollection={(collection) => {
                setCollectionToEdit(collection);
                setEditCollectionDialogOpen(true);
              }}
              onDeleteCollection={(collection) => {
                setCollectionToDelete(collection);
                setDeleteCollectionDialogOpen(true);
              }}
              onShareCollection={(collection) => {
                setCollectionToShare(collection);
                setShareDialogOpen(true);
              }}
              onCreateCollection={() => setShowCreateCollectionDialog(true)}
              emptyStateTitle="No collections yet"
              emptyStateDescription="Create your first collection to get started"
            />
          </div>
        </div>
      ) : (
        /* Documents View */
        <div className="flex-1 overflow-hidden">
          {selectedCollection && (
            <DocumentListView
              collection={selectedCollection}
              documents={documents}
              loading={loadingDocuments}
              rebuildingIndex={rebuildingIndex}
              onBack={handleBack}
              onUploadDocument={() => setShowUploadDocumentDialog(true)}
              onRebuildIndex={handleRebuildIndex}
              onDeleteDocument={(doc) => {
                setDocumentToDelete(doc);
                setDeleteDocumentDialogOpen(true);
              }}
              onDocumentsChange={() => {
                if (selectedCollection) {
                  loadDocuments(selectedCollection.id);
                }
              }}
            />
          )}
        </div>
      )}

      {/* Create Collection Dialog */}
      <Dialog open={showCreateCollectionDialog} onOpenChange={setShowCreateCollectionDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create New Collection</DialogTitle>
            <DialogDescription>
              Create a new knowledge base collection. Collections are private by default - you can share them after creation.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label className="text-sm font-medium mb-2 block">Name *</Label>
              <Input
                placeholder="My Collection"
                value={newCollectionForm.name}
                onChange={(e) =>
                  setNewCollectionForm({ ...newCollectionForm, name: e.target.value })
                }
              />
            </div>

            <div>
              <Label className="text-sm font-medium mb-2 block">Description</Label>
              <Textarea
                placeholder="Optional description for this collection..."
                value={newCollectionForm.description}
                onChange={(e) =>
                  setNewCollectionForm({ ...newCollectionForm, description: e.target.value })
                }
                rows={3}
              />
            </div>

            <div>
              <Label className="text-sm font-medium mb-2 block">Embedding Model</Label>
              <Select
                value={newCollectionForm.embeddingModel}
                onValueChange={(v) =>
                  setNewCollectionForm({ ...newCollectionForm, embeddingModel: v })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="text-embedding-3-small">
                    text-embedding-3-small (1536d, fast)
                  </SelectItem>
                  <SelectItem value="text-embedding-3-large">
                    text-embedding-3-large (3072d, accurate)
                  </SelectItem>
                  <SelectItem value="text-embedding-ada-002">
                    text-embedding-ada-002 (1536d, legacy)
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                The embedding model cannot be changed after creation
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateCollectionDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateCollection}
              disabled={creatingCollection || !newCollectionForm.name.trim()}
            >
              {creatingCollection ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Collection'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Document Dialog */}
      <Dialog
        open={showUploadDocumentDialog}
        onOpenChange={(open) => {
          setShowUploadDocumentDialog(open);
          if (!open) {
            setSelectedFile(null);
            setContentTab('upload');
          }
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Upload Document</DialogTitle>
            <DialogDescription>
              Add a document to "{selectedCollection?.name}". The content will be chunked and
              embedded for semantic search.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label className="text-sm font-medium mb-2 block">Document Name *</Label>
              <Input
                placeholder="My Document"
                value={uploadDocumentForm.name}
                onChange={(e) =>
                  setUploadDocumentForm({ ...uploadDocumentForm, name: e.target.value })
                }
              />
            </div>

            <div>
              <Label className="text-sm font-medium mb-2 block">Content *</Label>
              <Tabs
                value={contentTab}
                onValueChange={(v) => setContentTab(v as 'upload' | 'paste')}
              >
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="upload">Upload File</TabsTrigger>
                  <TabsTrigger value="paste">Paste Content</TabsTrigger>
                </TabsList>

                <TabsContent value="upload" className="mt-3">
                  {selectedFile ? (
                    <div className="border rounded-lg p-4 bg-muted/30">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                            <FileText className="w-5 h-5 text-primary" />
                          </div>
                          <div>
                            <p className="font-medium text-sm">{selectedFile.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {(selectedFile.size / 1024).toFixed(1)} KB •{' '}
                              {uploadDocumentForm.content.length.toLocaleString()} characters
                            </p>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => {
                            setSelectedFile(null);
                            setUploadDocumentForm((prev) => ({ ...prev, content: '' }));
                          }}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                      className={cn(
                        'border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer',
                        isDragging
                          ? 'border-primary bg-primary/5'
                          : 'border-muted-foreground/25 hover:border-muted-foreground/50'
                      )}
                      onClick={() => document.getElementById('file-upload-input')?.click()}
                    >
                      <input
                        id="file-upload-input"
                        type="file"
                        accept=".pdf,.docx,.txt,.md,.csv,.json,.xml,.html"
                        onChange={handleFileSelect}
                        className="hidden"
                      />
                      <Upload className="w-8 h-8 mx-auto text-muted-foreground mb-3" />
                      <p className="text-sm font-medium mb-1">
                        {isDragging ? 'Drop file here' : 'Drag and drop a file here'}
                      </p>
                      <p className="text-xs text-muted-foreground mb-3">or</p>
                      <Button variant="outline" size="sm" type="button">
                        Browse Files
                      </Button>
                      <p className="text-xs text-muted-foreground mt-3">
                        Supported: PDF, DOCX, TXT, MD, CSV, JSON (max 10MB)
                      </p>
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="paste" className="mt-3">
                  <Textarea
                    placeholder="Paste your document content here..."
                    value={uploadDocumentForm.content}
                    onChange={(e) => {
                      setUploadDocumentForm({ ...uploadDocumentForm, content: e.target.value });
                      setSelectedFile(null);
                    }}
                    rows={8}
                    className="font-mono text-sm"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    {uploadDocumentForm.content.length.toLocaleString()} characters
                  </p>
                </TabsContent>
              </Tabs>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="flex items-center gap-1 mb-2">
                  <Label className="text-sm font-medium">Chunk Size</Label>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <HelpCircle className="w-3.5 h-3.5 text-muted-foreground cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        <p className="font-medium mb-1">Chunk Size</p>
                        <p className="text-xs">
                          The maximum number of characters per chunk. Larger chunks preserve more
                          context but may reduce search precision. Recommended: 500-2000 characters.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <Input
                  type="number"
                  value={uploadDocumentForm.chunkSize}
                  onChange={(e) =>
                    setUploadDocumentForm({
                      ...uploadDocumentForm,
                      chunkSize: parseInt(e.target.value) || 1000,
                    })
                  }
                />
                <p className="text-xs text-muted-foreground mt-1">Characters per chunk</p>
              </div>
              <div>
                <div className="flex items-center gap-1 mb-2">
                  <Label className="text-sm font-medium">Chunk Overlap</Label>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <HelpCircle className="w-3.5 h-3.5 text-muted-foreground cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        <p className="font-medium mb-1">Chunk Overlap</p>
                        <p className="text-xs">
                          The number of characters that overlap between consecutive chunks. Overlap
                          helps preserve context across chunk boundaries. Recommended: 10-20% of
                          chunk size.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <Input
                  type="number"
                  value={uploadDocumentForm.chunkOverlap}
                  onChange={(e) =>
                    setUploadDocumentForm({
                      ...uploadDocumentForm,
                      chunkOverlap: parseInt(e.target.value) || 200,
                    })
                  }
                />
                <p className="text-xs text-muted-foreground mt-1">Overlap between chunks</p>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadDocumentDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleUploadDocument}
              disabled={
                uploadingDocument ||
                !uploadDocumentForm.name.trim() ||
                (!selectedFile && !uploadDocumentForm.content.trim())
              }
            >
              {uploadingDocument ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Upload Document
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Collection Confirmation */}
      <AlertDialog open={deleteCollectionDialogOpen} onOpenChange={setDeleteCollectionDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Collection</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the collection "{collectionToDelete?.name}"? This will
              permanently delete all documents and embeddings in this collection. This action cannot
              be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setCollectionToDelete(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteCollection}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete Collection
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Document Confirmation */}
      <AlertDialog open={deleteDocumentDialogOpen} onOpenChange={setDeleteDocumentDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Document</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the document "{documentToDelete?.name}"? This will
              remove all chunks and embeddings for this document. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDocumentToDelete(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteDocument}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete Document
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit Collection Dialog */}
      <EditCollectionDialog
        open={editCollectionDialogOpen}
        onOpenChange={(open) => {
          setEditCollectionDialogOpen(open);
          if (!open) setCollectionToEdit(null);
        }}
        collection={collectionToEdit}
        onSuccess={loadCollections}
      />

      {/* Share Collection Dialog */}
      <ShareCollectionDialog
        open={shareDialogOpen}
        onOpenChange={(open) => {
          setShareDialogOpen(open);
          if (!open) setCollectionToShare(null);
        }}
        collection={collectionToShare}
        teams={teams}
        onSuccess={loadCollections}
      />
    </div>
  );
}
